import { IGenericFilter } from '@/models/filter.model';
import { RouteLocationNormalized } from 'vue-router';

export default function useHashParams(route: RouteLocationNormalized) {
  function _setURLSearchParamsToHash(urlParams: URLSearchParams, refresh = true) {
    // set to hash as query params
    // '?' is required for vue-router to recognize as query params in hash
    // Setting hash will refresh the site. We can use history.pushState to disable refreshing if needed
    /**
     * Note:
     *  If refresh is set to false, route.query will not be updated although the url in the address bar is changed
     *  This might cause problem when you depend on route.query to get the updated hash parameters
     *  window.history.pushState DOES NOT change route.query and thus, will not trigger watches on it
     * */
    if (refresh) {
      if (urlParams.toString() === '') {
        window.location.hash = ' ';
      } else {
        window.location.hash = '?' + urlParams.toString();
      }
    } else {
      /**
       * This is required if you want to modify the URL without affecting route.query
       * if the page refreshes inside route.query's watcher,
       *  the watcher will trigger again, which in turn, trigger the code again -> endless recursive
       * */
      let newUrl = window.location.protocol + '//' + window.location.host + window.location.pathname;
      // T19128: For ZWS, we need to also append the query parameter since their base URL has query parameter
      if (window.location.search && window.location.search != '') {
        newUrl += window.location.search;
      }
      const params = urlParams.toString();
      if (params !== '') {
        newUrl += '#?' + params;
      }
      window.history.pushState({ path: newUrl }, '', newUrl);
    }
  }

  // set given HashParams to URI
  // update given Params in Hash without changes to other params
  // required if multiple component instances on same page rely on different params
  function setHashParams(params: IGenericFilter, refresh = true) {
    // use URLSearchParams to convert to query string
    const urlParams = new URLSearchParams();

    // add provided params
    for (const key in params) {
      if (params[key] != null && params[key] != undefined) {
        // only assign non-null or defined value
        urlParams.set(key, params[key]);
      }
    }

    // add any missing params that are already defined in route.query
    for (const [key, value] of Object.entries(route.query)) {
      if (
        !urlParams.has(key) &&
        // If the value to be assigned is null or undefined, we should not add it to hashparameter
        !(params[key] == null || params[key] == undefined)
      ) {
        let stringValue = '';
        if (Array.isArray(value)) {
          stringValue = value.join(',');
        } else {
          stringValue = value === null || value === undefined ? '' : value.toString();
        }
        urlParams.set(key, stringValue);
      }
    }

    _setURLSearchParamsToHash(urlParams, refresh);
  }

  // remove HashParam from URI
  function deleteHashParams(paramKey: string[], refresh = true) {
    const urlParams = new URLSearchParams();
    // check params defined in route.query and remove provided key
    for (const [key, value] of Object.entries(route.query)) {
      paramKey.forEach((val: any) => {
        if (
          key !==
          paramKey.filter((hash: any) => {
            return hash === key;
          })[0]
        ) {
          if (key != val) {
            let stringValue = '';
            if (Array.isArray(value)) {
              stringValue = value.join(',');
            } else {
              stringValue = value === null ? '' : value.toString();
            }
            urlParams.set(key, stringValue);
          }
        }
      });
    }

    _setURLSearchParamsToHash(urlParams, refresh);
  }

  function deleteHashParam(paramKey: string, refresh = true) {
    const urlParams = new URLSearchParams();

    // check params defined in route.query and remove provided key
    for (const [key, value] of Object.entries(route.query)) {
      if (key != paramKey) {
        let stringValue = '';
        if (Array.isArray(value)) {
          stringValue = value.join(',');
        } else {
          stringValue = value === null ? '' : value.toString();
        }
        urlParams.set(key, stringValue);
      }
    }

    _setURLSearchParamsToHash(urlParams, refresh);
  }

  // Simply returns route.query
  function getHashParams() {
    return route.query;
  }

  return {
    setHashParams,
    deleteHashParams,
    deleteHashParam,
    getHashParams,
  };
}
